import { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';

import styles from '../home.module.css';
import type { HomeViewProps } from '../home.types';
import clsx from 'clsx';
import { Analytics } from 'lib/AnalyticsLoader/analytics';

import MobileLayout from 'shared/components/layout/mobile-layout';
import { Avatar } from 'shared/components/ui/Avatar/Avatar';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import { ROUTES } from 'shared/constants';
import { DISCORD_USERNAME_LENGTH } from 'shared/constants/constants';
import { SOCIAL_MEDIA } from 'shared/constants/socialMedia';
import { useGlobalStore } from 'shared/store/globalStore';
import { prettyTx } from 'shared/utils/pretty-tx';

import ArrowDown from 'assets/arrow.svg?react';
import ImageJoinDiscord from 'assets/landing/discord.png?format=webp;avif&as=picture';
import ImageLeaderBoard from 'assets/landing/leaderboard.png?format=webp;avif&as=picture';
import ImageNFTs from 'assets/landing/nfts.png?format=webp;avif&as=picture';
import ImagePlayGame from 'assets/landing/playgame.png?format=webp;avif&as=picture';
import ImagePrizeBox from 'assets/landing/prizebox.png?format=webp;avif&as=picture';
import ImageQuests from 'assets/landing/quests.png?format=webp;avif&as=picture';
import attention from 'assets/png/!.png?format=webp;avif&as=picture';

export const MobileView: FC<HomeViewProps> = ({
  title,
  chooseText,
  yourChainText,
  address,
  pointBalance,
  attemptsLeft,
  activeQuests,
  currentChain,
  chains,
  networkOpen,
  isConnected,
  setNetworkOpen,
  onChainSelect,
  handleWalletConnect,
}) => {
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const screenAddress = useMemo(
    () =>
      prettyTx({
        address,
        leaderboardMap,
        discordUsernameLength: DISCORD_USERNAME_LENGTH,
      }),
    [address, leaderboardMap],
  );
  return (
    <MobileLayout title={title} footer={<></>} showFloor={false}>
      <div className="mb-8 space-y-3">
        <div>
          {!isConnected && (
            <PrimaryButton
              onClick={handleWalletConnect}
              text="common.connectWallet"
              isAnimated
              className="w-full"
            />
          )}
          {isConnected && [
            <div
              key="avatarMobile"
              className="flex items-center justify-between gap-4 rounded-t-2xl border border-border bg-gradient-to-b from-grape to-[#030220] p-1.5"
            >
              <div className="flex items-center gap-1">
                <Avatar
                  userAddress={address?.toLowerCase() as `0x${string}`}
                  className="h-8 w-8 rounded-[10px]"
                />
                <p className="text-sm font-semibold text-orange-btn">{screenAddress}</p>
              </div>
              <InfiniteMaxProgressBar value={Number(pointBalance)} showAttention />
            </div>,
            <div
              key="chainSelectorMobile"
              className="flex items-center justify-between gap-5 rounded-b-2xl border border-t-0 border-border bg-gradient-to-b from-one-more-blue to-[#0F0AAB] p-1.5 pl-5"
            >
              <div>
                <p className={styles['text-gradient-stroke']}>{chooseText}</p>
                <p className={clsx(styles['text-gradient-stroke'], 'text-md')}>{yourChainText}</p>
              </div>

              <div className="relative flex flex-col items-center justify-center gap-1 rounded-[10px] bg-move-icon-border p-[1px]">
                <div className="overflow-hidden rounded-[10px] bg-[linear-gradient(180deg,_#0D1450_0%,_#172077_100%)] p-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setNetworkOpen(!networkOpen);
                    }}
                    className="flex w-full items-center justify-between gap-2.5 pr-2 text-white"
                  >
                    <span className="flex items-center gap-2 text-md font-semibold leading-3 text-white">
                      {currentChain?.icon && (
                        <div className="flex items-center justify-center rounded-lg bg-[#12AAFF] p-1">
                          <img src={currentChain.icon} className="h-5 w-5 rounded-full" />
                        </div>
                      )}
                      {currentChain?.name ?? '—'}
                    </span>
                    <ArrowDown
                      className={`${networkOpen ? 'rotate-180 transform' : ''} ml-4 w-3 transition duration-500 ease-in-out`}
                    />
                  </button>

                  {networkOpen && (
                    <div className="absolute -right-1 z-10 mt-2 w-52 rounded-lg bg-[#0D1450] p-2 shadow-lg">
                      {chains.map((c) => (
                        <button
                          key={c.id}
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-white hover:bg-white/10"
                          onClick={() => onChainSelect(c.id)}
                        >
                          {c.icon && <img src={c.icon} className="h-5 w-5 rounded-full" />}
                          <span>{c.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>,
          ]}
        </div>

        <hr className="border border-indigo" />

        <div className="flex flex-col items-center justify-center gap-2">
          <Link
            to={`${ROUTES.LOBBY}${window.location.search}`}
            className="flex w-full"
            onClick={() => Analytics.homePageItemClickEvent('Lobby Link Click')}
          >
            <OptimizedImage imageData={ImagePlayGame} alt="play game" className="w-full" />
          </Link>
          <div className="flex items-start justify-center gap-2">
            <a
              href={SOCIAL_MEDIA.DISCORD}
              onClick={() => Analytics.homePageItemClickEvent('Discord Link Click')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full"
            >
              <OptimizedImage imageData={ImageJoinDiscord} alt="join discord" className="w-full" />
            </a>
            <Link
              to={ROUTES.PRIZE_BOX}
              className="relative flex w-full"
              onClick={() => Analytics.homePageItemClickEvent('PrizeBox Link Click')}
            >
              <OptimizedImage imageData={ImagePrizeBox} alt="prize box" className="w-full" />
              {attemptsLeft > 0 && (
                <div className={clsx(styles.circle, 'absolute -bottom-1 -right-1')}>
                  {attemptsLeft}
                </div>
              )}
            </Link>
          </div>
        </div>

        <hr className="border border-indigo" />

        <div className="flex flex-col items-center justify-center gap-2">
          <div className="flex items-center justify-center gap-2">
            <Link
              to={ROUTES.DAILY_QUESTS}
              className="relative flex w-full"
              onClick={() => Analytics.homePageItemClickEvent('Quests Link Click')}
            >
              <OptimizedImage imageData={ImageQuests} alt="quests" className="w-full" />
              {activeQuests > 0 && (
                <OptimizedImage
                  imageData={attention}
                  alt="attention"
                  className="absolute -bottom-1 -right-1 h-6 w-6 animate-badgePop motion-reduce:animate-none"
                />
              )}
            </Link>
            <Link
              to={ROUTES.NFTS}
              className="relative flex w-full"
              onClick={() => Analytics.homePageItemClickEvent('NFT Link Click')}
            >
              <OptimizedImage imageData={ImageNFTs} alt="nfts" className="w-full" />
              {Number(pointBalance) >= 100 && (
                <OptimizedImage
                  imageData={attention}
                  alt="attention"
                  className="absolute -bottom-1 -right-1 h-6 w-6 animate-badgePop motion-reduce:animate-none"
                />
              )}
            </Link>
          </div>
          <Link
            to={ROUTES.LEADERBOARD}
            className="flex w-full"
            onClick={() => Analytics.homePageItemClickEvent('Leaderboard Link Click')}
          >
            <OptimizedImage imageData={ImageLeaderBoard} alt="leaderboard" className="w-full" />
          </Link>
        </div>
      </div>
    </MobileLayout>
  );
};
